import { Activity, ActivityFilterEnum, CollectionNameEnum, Expense, OperationTypeEnum } from "@finway-group/shared/lib/models"
import { Badge, Button, Dropdown, Empty, Menu, Radio } from "antd"
import { RadioChangeEvent } from "antd/lib/radio/interface"
import moment from "moment"
import React, { Suspense, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"

import Icon from "Components/icon"
import { SpreadsheetDataUploadModal } from "Components/modals/spreadsheetDataUpload/spreadsheetDataUpload.modal"
import { isFullyProcessedSubscription } from "Features/pages/expenses/expenseUtils"
import { useModal } from "Shared/context/modal.context"
import { useLoggedInEmployee } from "Shared/hooks/employee.hooks"
import { NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { fetchAllActivities, markActivityAsViewed, markAllAsViewed } from "Shared/store/actions/activity/activityActions"
import { RootState } from "Shared/store/rootState.interface"
import { getPopupAnchor } from "Shared/utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import ActivitySkeleton from "./activity.skeleton"
import { downloadFileAsSignedUrl } from "Shared/utils/helpers"

const ActivityCard = React.lazy(() => import("./activityCard"))

const ActivityContainer = ({}) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { showModal } = useModal()
    const scrollEvent = useRef<any>()

    const [activityList, setActivityList] = useStateIfMounted<Activity[]>([])
    const [notificationCount, setNotificationCount] = useStateIfMounted(0)
    const [activityFilter, setActivityFilter] = useStateIfMounted(ActivityFilterEnum.UNREAD)
    const [page, setPage] = useStateIfMounted<number>(1)
    const [pageIsLoading, setIsPageLoading] = useStateIfMounted<boolean>(false)

    let loggedInUser = useLoggedInEmployee()
    const loggedInUserId = useSelector(({ auth }: RootState) => auth.user)?.id // to avoid the case where loggedInUser.id is undefined before being correctly determined.

    const nonViewedFilter = (activity: Activity) => loggedInUserId && activity.viewedBy.indexOf(loggedInUserId) < 0
    const newActivityFilter = (activity: Activity) => moment(activity.created).isSame(moment(), "day")
    const oldActivityFilter = (activity: Activity) => !moment(activity.created).isSame(moment(), "day")

    const filterSystemActivity = ({ createdBy, documentId, operationType }: Activity) => !(createdBy === documentId && operationType !== OperationTypeEnum.INSERT)
    const filterExternalActivity = ({ collectionName }: Activity) =>
        ![CollectionNameEnum.CURRENCY_EXCHANGE, CollectionNameEnum.COMPANY_SETTINGS].includes(collectionName as CollectionNameEnum)
    const filterNonNotificationActivity = ({ isNotification }: any) => !!isNotification

    let { activities, totalUnseen, totalPages } = useSelector(({ activity: { items, totalUnseen, totalPages } }: RootState) => ({ activities: items, totalUnseen, totalPages }))
    activities = activities.filter((activity) => filterNonNotificationActivity(activity) && filterExternalActivity(activity) && filterSystemActivity(activity))

    useEffect(() => {
        let mounted = true

        if (mounted) {
            setNotificationCount(totalUnseen)
            updateActivityView()
        }

        return () => {
            mounted = false
        }
    }, [totalUnseen, JSON.stringify(activities)])

    useEffect(() => {
        let mounted = true
        const fetchPage = async () => {
            await dispatch(fetchAllActivities(page, true))
            setIsPageLoading(false)
        }

        if (mounted && page > 1 && page <= totalPages) {
            fetchPage()
        }

        return () => {
            mounted = false
        }
    }, [page])

    useEffect(() => {
        updateActivityView()
    }, [activityFilter, activities.length])

    const loadNextPage = () => {
        if (totalPages >= page + 1) {
            setIsPageLoading(true)
            setPage(page + 1)
        }
    }

    const updateActivityView = () => {
        switch (activityFilter) {
            case ActivityFilterEnum.UNREAD:
                const nonViewedActivities = activities.filter(nonViewedFilter)
                setActivityList(nonViewedActivities)

                if (nonViewedActivities.length < 10 && totalUnseen > nonViewedActivities.length) {
                    loadNextPage()
                }
                break
            default:
                setActivityList(activities)

                if (activities.length < 10 && activities.length > 0) {
                    loadNextPage()
                }

                break
        }
    }

    const getActivityDocumentRoute = async ({ documentId, collectionName, operationType }: any) => {
        switch (collectionName) {
            case CollectionNameEnum.EXPENSE:
                const expense = new Expense(documentId)
                return isFullyProcessedSubscription(expense) ? "subscriptions" : "expenses"
            case CollectionNameEnum.VENDOR:
                return "vendors"
            case CollectionNameEnum.USER:
                return "employees"
            case CollectionNameEnum.COST_CENTER:
                return "costCenters"
            case CollectionNameEnum.INBOX_INVOICE:
                return "expenses"
            case CollectionNameEnum.BANK_CONNECTION:
                if (operationType === OperationTypeEnum.BANK_PAYMENT_SUCCESS) return "expenses"
                return "settings"
            case CollectionNameEnum.CARD:
                if (operationType === OperationTypeEnum.EXPENSE_CARD_TRANSACTION) return "expenses"
                return "credit-cards"
            case CollectionNameEnum.WORKFLOW:
                return "workflows"
            default:
                return ""
        }
    }

    /**
     * Handle all activity item click
     * @param param0
     * @returns
     */
    const activityItemSelected = async ({ key }: any) => {
        if (key === "empty") return
        const activity = activities.find((activity) => activity.id === key)

        if (activity) {
            if (nonViewedFilter(activity)) {
                activity.viewedBy.push(loggedInUser.id)
                await dispatch(markActivityAsViewed(activity))
                notificationCount > 0 && setNotificationCount(notificationCount - 1)
            }

            updateActivityView()

            // for data uploads we show a modal on click instead of redirecting to a page
            if (activity?.collectionName === CollectionNameEnum.IMPORT_RESULT) {
                showModal(SpreadsheetDataUploadModal, true, { isShowing: true, importResultId: (activity.documentId as any)._id })
                return
            }

            // for download notifications, execute the download instead of redirecting to a page
            if (activity?.operationType === OperationTypeEnum.ASYNC_EXPORT) {
                downloadFileAsSignedUrl((activity.metaData as any)?.fileUrl)
                return
            }

            const collectionName = await getActivityDocumentRoute(activity)
            if (collectionName.length === 0) {
                NotificationService.send(NotificationTypeEnum.WARNING, t("error:notifications.title"), t("error:notifications.message"))
                return
            }

            const route = {
                collectionName,
                documentId: (activity.documentId as any)?._id,
            }
            history.push(`/${route.collectionName}/${route.documentId}`)
        }
    }

    /**
     * Empty Menu List template
     */
    const renderEmptyList = (
        <Menu.Item key="empty">
            <Empty
                image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                imageStyle={{
                    height: 60,
                    margin: "auto",
                }}
                description={<span>{t("label:no_notifications")}</span>}
            ></Empty>
        </Menu.Item>
    )

    const markAllActivitiesAsViewed = async () => {
        const ids = activities.filter(nonViewedFilter).map((activity) => activity.id)
        await dispatch(markAllAsViewed(ids, loggedInUser.id))

        setNotificationCount(0)
        setActivityFilter(ActivityFilterEnum.ALL)
    }

    const handleFilterChange = (e: RadioChangeEvent) => {
        setActivityFilter(e.target.value)
        switch (e.target.value) {
            case ActivityFilterEnum.UNREAD:
                setActivityList(activities.filter(nonViewedFilter))
                break
            default:
                setActivityList(activities)
                break
        }
    }

    const menuFilterGroup = (
        <Menu.ItemGroup key="group-filter" title="">
            <div className="w-full py-5 flex justify-between">
                <Radio.Group onChange={handleFilterChange} defaultValue={ActivityFilterEnum.UNREAD} value={activityFilter}>
                    <Radio.Button className="m-5" value={ActivityFilterEnum.UNREAD}>
                        {t("label:unread")}
                    </Radio.Button>

                    <Radio.Button className="m-5" value={ActivityFilterEnum.ALL}>
                        {t("label:all")}
                    </Radio.Button>
                </Radio.Group>

                <Button type="default" onClick={markAllActivitiesAsViewed} className="m-5 pl-4 pr-4 max-w-160 truncate inline-block">
                    {t("label:mark_all_as_read")}
                </Button>
            </div>
        </Menu.ItemGroup>
    )

    const PageLoadingSkeleton = () => (
        <Menu.Item key="loadingSkeletonKey" className="min-w-350">
            <ActivitySkeleton />
        </Menu.Item>
    )

    const NotificationMenu = (
        <Menu className="z-20 notification-dropdown w-380 overscroll-y-auto" onClick={activityItemSelected}>
            {menuFilterGroup}

            {activityList.filter(newActivityFilter).length > 0 && (
                <Menu.ItemGroup key="group-new" title={t("label:notifications.most_recent")}>
                    {activityList.filter(newActivityFilter).map((activity: Activity) => (
                        <Menu.Item key={activity.id} className="min-w-350">
                            <Suspense fallback={<ActivitySkeleton />}>
                                <ActivityCard key={activity.id} activity={activity} loggedInUser={loggedInUser} />
                            </Suspense>
                        </Menu.Item>
                    ))}
                    {pageIsLoading && <PageLoadingSkeleton />}
                </Menu.ItemGroup>
            )}

            {activityList.filter(oldActivityFilter).length > 0 && (
                <Menu.ItemGroup key="group-old" title={t("label:notifications.older")}>
                    {activityList.filter(oldActivityFilter).map((activity: Activity) => (
                        <Menu.Item key={activity.id} className="min-w-350">
                            <Suspense fallback={<ActivitySkeleton />}>
                                <ActivityCard key={activity.id} activity={activity} loggedInUser={loggedInUser} />
                            </Suspense>
                        </Menu.Item>
                    ))}
                    {pageIsLoading && <PageLoadingSkeleton />}
                </Menu.ItemGroup>
            )}
            {activityList.length === 0 && renderEmptyList}
        </Menu>
    )

    const handleScroll = (event: any) => {
        const e = event.nativeEvent
        if (scrollEvent.current) {
            if (e.target.scrollTop + 10 >= e.target.scrollHeight - e.target.clientHeight && totalPages >= page + 1 && !pageIsLoading) {
                loadNextPage()
            }
        }
    }

    return (
        <div className="notification-activity" ref={scrollEvent} onScroll={handleScroll}>
            <Dropdown
                className={"flex items-center mr-20"}
                trigger={["click"]}
                overlay={NotificationMenu}
                onVisibleChange={(visible) => !visible && setActivityFilter(ActivityFilterEnum.UNREAD)}
                getPopupContainer={getPopupAnchor()}
            >
                <Badge size="small" count={notificationCount} offset={[-10, 8]}>
                    <Icon className="cursor-pointer" icon="activity_bell" />
                </Badge>
            </Dropdown>
        </div>
    )
}

export default React.memo(ActivityContainer)
